import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import { BsFillCaretDownFill, BsXLg } from "react-icons/bs";
import { GoPaperclip } from "react-icons/go";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import css from "./SupportModal.module.css";
import myIcon from "../../../assets/icons.svg";
import { DepartmentPopover } from "./DepartmentPopover/DepartmentPopover";
import { createTicket } from "../../../redux/chat/operations";
import { fileToBase64 } from "../../../utils/convertInBase64";
import { showToast } from "../../../utils/showToast";
import { useImageModal } from "../../../utils/useImageModal.jsx";
import FullSizeImgModal from "../FullSizeImgModal/FullSizeImgModal.jsx";

export const validationSchema = Yup.object().shape({
  title: Yup.string().required("Введіть тему"),
  body: Yup.string().required("Опишіть проблему"),
  department: Yup.string().required("Оберіть відділ"),
  priority: Yup.string().required("Оберіть пріоритет"),
  type: Yup.string().oneOf(["bug", "proposition"]).required(),
  module: Yup.string().required("Оберіть модуль"),
});

export const departmentOptions = [
  {
    id: 1,
    value: "IT",
    label: "Технічна підтримка, Сервери, ПЗ, Бази даних",
  },
  {
    id: 2,
    value: "HR",
    label: "Кадровий відділ, Питання щодо персоналу",
  },
  {
    id: 3,
    value: "Finance",
    label: "Фінансовий відділ, Платежі, Рахунки",
  },
  {
    id: 4,
    value: "Partnership",
    label: "Партнерство та співпраця",
  },
];

export const priorityOptions = [
  { value: "low", label: "Низька" },
  { value: "medium", label: "Середня" },
  { value: "high", label: "Висока" },
];

export const moduleOptions = [
  { value: "Моніторинг", label: "Моніторинг" },
  { value: "Планувальник", label: "Планувальник" },
  { value: "В роботі", label: "В роботі" },
  { value: "Облік", label: "Облік" },
  { value: "Звіти", label: "Звіти" },
  { value: "Налаштування", label: "Налаштування" },
];

const MAX_IMAGES = 5;

export default function SupportModal({ onClose }) {
  const dispatch = useDispatch();
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [isModulePopoverOpen, setModulePopoverOpen] = useState(false);

  // Для завантаження скрінів (до MAX_IMAGES штук)
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]); // [{ id, name, base64, previewUrl }]
  const imagesRef = useRef([]); // актуальний список для async-обробників
  const [fileError, setFileError] = useState(null);
  const { isImgModalOpen, modalImage, handleImageClick, closeImgModal } =
    useImageModal();

  const popoverRef = useRef(null);
  const modulePopoverRef = useRef(null);
  const selectRef = useRef(null);
  const moduleSelectRef = useRef(null);

  const initialValues = {
    title: "",
    body: "",
    department: "",
    priority: "medium",
    photo_base64: "",
    type: "bug",
    module: "",
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        isPopoverOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        !selectRef.current?.contains(e.target)
      ) {
        setPopoverOpen(false);
      }

      if (
        isModulePopoverOpen &&
        modulePopoverRef.current &&
        !modulePopoverRef.current.contains(e.target) &&
        !moduleSelectRef.current?.contains(e.target)
      ) {
        setModulePopoverOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopoverOpen, isModulePopoverOpen]);

  const updateImages = updater => {
    const next = updater(imagesRef.current);
    imagesRef.current = next;
    setImages(next);
  };

  // Додає зображення з урахуванням ліміту MAX_IMAGES
  const addImages = async files => {
    const imageFiles = files.filter(file => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      if (files.length > 0) setFileError("Потрібно обрати зображення");
      return;
    }

    const freeSlots = MAX_IMAGES - imagesRef.current.length;
    if (freeSlots <= 0) {
      setFileError(`Максимум ${MAX_IMAGES} зображень`);
      return;
    }

    const filesToAdd = imageFiles.slice(0, freeSlots);

    try {
      const newImages = await Promise.all(
        filesToAdd.map(async file => ({
          id: `${Date.now()}-${Math.random()}`,
          name: file.name,
          base64: await fileToBase64(file),
          previewUrl: URL.createObjectURL(file),
        }))
      );

      // Повторна перевірка ліміту — поки конвертувалось, могли додати ще
      updateImages(prev => {
        const accepted = newImages.slice(0, MAX_IMAGES - prev.length);
        newImages
          .slice(accepted.length)
          .forEach(img => URL.revokeObjectURL(img.previewUrl));
        return [...prev, ...accepted];
      });

      setFileError(
        imageFiles.length > filesToAdd.length
          ? `Максимум ${MAX_IMAGES} зображень`
          : null
      );
    } catch (error) {
      console.error("Error converting image:", error);
    }
  };

  useEffect(() => {
    const handlePaste = event => {
      const items = event.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) addImages(files);
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, []);

  // Звільняємо object URL прев'ю при закритті модалки
  useEffect(() => {
    return () => {
      imagesRef.current.forEach(img => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  const handleFileChange = e => {
    const files = Array.from(e.target.files || []);
    // Скидаємо value, щоб можна було повторно обрати той самий файл
    e.target.value = "";
    if (files.length === 0) return;
    addImages(files);
  };

  const removeImage = id => {
    updateImages(prev => {
      const removed = prev.find(img => img.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter(img => img.id !== id);
    });
    setFileError(null);
  };

  const handleSubmit = (values, { resetForm }, dispatch, onClose) => {
    try {
      // Перше зображення — в image_base64, решта — в images_base64
      const [firstImage, ...restImages] = imagesRef.current;
      const payload = {
        ...values,
        image_base64: firstImage?.base64 || "",
        images_base64: restImages.map(img => img.base64),
      };

      dispatch(createTicket(payload))
        .unwrap()
        .then(() => {
          showToast("Ваше звернення прийнято. Дякуємо!", "success");
        });

      resetForm();
      onClose();
    } catch (error) {
      console.error("Error while creating ticket:", error);
    }
  };

  return (
    <>
      <svg className={css.svg}>
        <use href={`${myIcon}#icon-support`} className={css.icon}></use>
      </svg>

      <div className={css.contentBox}>
        <BsXLg className={css.closeBtn} onClick={onClose} />
        <p className={css.title}>Технічна підтримка</p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, formikHelpers) =>
            handleSubmit(values, formikHelpers, dispatch, onClose)
          }
        >
          {({ values, errors, touched, setFieldValue }) => {
            return (
              <Form className={css.form}>
                <div className={css.fieldSubject}>
                  <p className={css.label}>Тема повідомлення</p>
                  <Field
                    type="text"
                    name="title"
                    placeholder="Проблема з підключенням..."
                    className={css.input}
                  />
                  {errors.title && touched.title && (
                    <div className={css.error}>{errors.title}</div>
                  )}
                </div>

                <div className={css.fieldDepartment}>
                  <p className={css.label}>Оберіть відділ</p>
                  <div className={css.selectBox} ref={selectRef}>
                    <div
                      className={css.selectTrigger}
                      onClick={() => setPopoverOpen(prev => !prev)}
                    >
                      <Field
                        type="text"
                        name="department"
                        placeholder="Технічна підтримка..."
                        readOnly
                        value={
                          departmentOptions.find(
                            dept => dept.value === values.department
                          )?.label || ""
                        }
                        className={css.selectedDepartment}
                      />

                      <button className={css.btnArrow} type="button">
                        <BsFillCaretDownFill
                          className={`${css.iconArrow} ${
                            isPopoverOpen ? css.rotated : ""
                          }`}
                        />
                      </button>
                    </div>
                    {isPopoverOpen && (
                      <div ref={popoverRef}>
                        <DepartmentPopover
                          statuses={departmentOptions.map(dept => ({
                            status: dept.label,
                            id: dept.id,
                          }))}
                          onStatusSelect={selectedText => {
                            const selectedSubcategory = departmentOptions.find(
                              sub => sub.label === selectedText
                            );

                            if (selectedSubcategory) {
                              setFieldValue(
                                "department",
                                selectedSubcategory.value
                              );
                              setPopoverOpen(false);
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {errors.department && touched.department && (
                    <div className={css.error}>{errors.department}</div>
                  )}
                </div>

                <div className={css.fieldDepartment}>
                  <p className={css.label}>Оберіть модуль</p>

                  <div className={css.selectBox} ref={moduleSelectRef}>
                    <div
                      className={css.selectTrigger}
                      onClick={() => setModulePopoverOpen(prev => !prev)}
                    >
                      <Field
                        type="text"
                        name="module"
                        placeholder="Оберіть модуль..."
                        readOnly
                        value={
                          moduleOptions.find(mod => mod.value === values.module)
                            ?.label || ""
                        }
                        className={css.selectedDepartment}
                      />

                      <button className={css.btnArrow} type="button">
                        <BsFillCaretDownFill
                          className={`${css.iconArrow} ${
                            isModulePopoverOpen ? css.rotated : ""
                          }`}
                        />
                      </button>

                      {isModulePopoverOpen && (
                        <div
                          ref={modulePopoverRef}
                          onClick={e => e.stopPropagation()}
                        >
                          <DepartmentPopover
                            statuses={moduleOptions.map(mod => ({
                              status: mod.label,
                              id: mod.value,
                            }))}
                            onStatusSelect={selectedText => {
                              const selected = moduleOptions.find(
                                m => m.label === selectedText
                              );

                              if (selected) {
                                setFieldValue("module", selected.value);
                                setModulePopoverOpen(false);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {errors.module && touched.module && (
                    <div className={css.error}>{errors.module}</div>
                  )}
                </div>

                <div className={css.fieldPriority}>
                  <p className={css.label}>Пріоритетність проблеми</p>
                  <div className={css.radioGroup}>
                    {priorityOptions.map(({ value, label }) => (
                      <label key={value} className={css.radioLabel}>
                        <Field
                          type="radio"
                          name="priority"
                          value={value}
                          className={css.radio}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className={css.fieldPriority}>
                  <p className={css.label}>Тип звернення</p>
                  <div className={css.radioGroup}>
                    <label className={css.radioLabel}>
                      <Field
                        type="radio"
                        name="type"
                        value="bug"
                        className={css.radio}
                      />
                      Баг
                    </label>
                    <label className={css.radioLabel}>
                      <Field
                        type="radio"
                        name="type"
                        value="proposition"
                        className={css.radio}
                      />
                      Пропозиція
                    </label>
                  </div>
                </div>

                <div className={css.fieldTextarea}>
                  <Field
                    as="textarea"
                    name="body"
                    placeholder="Ваше повідомлення..."
                    className={css.textarea}
                  />
                  {errors.body && touched.body && (
                    <div className={css.error}>{errors.body}</div>
                  )}
                </div>
                <div className={css.btnContainer}>
                  <div className={css.fileUploadContainer}>
                    <button
                      type="button"
                      className={css.attachmentBtn}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={images.length >= MAX_IMAGES}
                    >
                      <GoPaperclip className={css.iconClips} /> Додати скрін
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    {images.length > 0 && (
                      <div className={css.previewList}>
                        {images.map(img => (
                          <div key={img.id} className={css.previewItem}>
                            <img
                              src={img.previewUrl}
                              alt={img.name || "Прев’ю зображення"}
                              className={css.imagePreview}
                              onClick={() => handleImageClick(img.previewUrl)}
                            />
                            <button
                              type="button"
                              className={css.removePreviewDoc}
                              onClick={() => removeImage(img.id)}
                            >
                              <BsXLg className={css.removeIcon} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {fileError && <p className={css.error}>{fileError}</p>}
                  </div>

                  <button type="submit" className={css.supportBtn}>
                    Надіслати
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      <FullSizeImgModal
        isOpen={isImgModalOpen}
        imgUrl={modalImage}
        onClose={closeImgModal}
      />
    </>
  );
}
